import { Injectable } from "@angular/core";

import { combineLatest, forkJoin, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";

import { dbToDate } from "@dffedb/util";
import { AppserverHttpClient, BrugerHttpClient, InstallationHttpClient, ParametreForAktuelleInstallation } from "@e-forsyning/common/http";
import { AppserverInstallationCredentialsRepository } from "@e-forsyning/common/credentials";
import { SilentLoginService } from "../silent-login";

import { DbEdhDokument } from "./model/db-edh-dokument.model";
import { EdhDokument } from "./model/edh-dokument.model";

@Injectable({ providedIn: "root" })
export class EdhRepository {
    constructor(
        private readonly brugerHttp: BrugerHttpClient,
        private readonly installationHttp: InstallationHttpClient,
        private readonly installationCredentials: AppserverInstallationCredentialsRepository,
        private readonly silentLoginService: SilentLoginService
    ) {}

    public hentDokumenterForAktuelInstallation(antalAarTilbage?: number): Observable<EdhDokument[]> {
        // Tjek om ejendomNr er sat - hvis ikke, returner tom array
        const credentials = this.installationCredentials.hent();
        if (!credentials || !credentials.ejendomNr) {
            return of([]);
        }

        const appServerUrl$ = this.installationHttp.getUrl("[URL]", ParametreForAktuelleInstallation.true);

        // Tilføj antalAarTilbage parameter til API kaldet hvis det er angivet
        let apiUrl = "api/edhliste";
        if (antalAarTilbage !== undefined && antalAarTilbage !== null) {
            apiUrl += `?antalAarTilbage=${antalAarTilbage}`;
        }

        const edhDokumenter$ = this.installationHttp.get(apiUrl, ParametreForAktuelleInstallation.true);
        return combineLatest([appServerUrl$, edhDokumenter$]).pipe(
            map((result: any) => {
                const appServerUrl: string = result[0];
                const dokumenter: DbEdhDokument[] = result[1].edhliste;
                const markerDokumentLaest = !this.silentLoginService.erLoggetPaaViaSilentLogin;
                return dokumenter
                    .map((d: DbEdhDokument) => ({
                        nr: d.nr,
                        dato: dbToDate(d.refdato) || dbToDate(d.tilfoejetdato),
                        overskrift: d.overskrift,
                        url: appServerUrl.replace("[URL]", `api/edh/documentid/${d.nr}?markerDokumentLaest=${markerDokumentLaest}`),
                        laest: d.laest,
                        laestDato: dbToDate(d.laest_dato) || null
                    }))
                    .sort((a: EdhDokument, b: EdhDokument) => b.dato.getTime() - a.dato.getTime() || b.nr - a.nr);
            })
        );
    }

    public markerAlleDokumenterLaest(): Observable<void> {
        // Tjek om ejendomNr er sat - hvis ikke, returner tom observable
        const credentials = this.installationCredentials.hent();
        if (!credentials || !credentials.ejendomNr) {
            return of(undefined);
        }

        return this.installationHttp.post("api/EdhAltLaest", {}, ParametreForAktuelleInstallation.true);
    }

    public hentDokumentUrlerForAktuelBruger(dokumentnummer: number): Observable<string[]> {
        const markerDokumentLaest = !this.silentLoginService.erLoggetPaaViaSilentLogin;
        return this.hentDokumentNumre(this.brugerHttp, dokumentnummer).pipe(
            switchMap((numre: string[]) =>
                forkJoin(
                    numre.map((nr: string) =>
                        this.brugerHttp.getUrl(`api/edh/documentid/${nr}?markerDokumentLaest=${markerDokumentLaest}`, ParametreForAktuelleInstallation.true)
                    )
                )
            )
        );
    }

    public hentDokumentUrlerForAktuelInstallation(dokumentnummer: number): Observable<string[]> {
        // Tjek om ejendomNr er sat - hvis ikke, returner tom array
        const credentials = this.installationCredentials.hent();
        if (!credentials || !credentials.ejendomNr) {
            return of([]);
        }

        const markerDokumentLaest = !this.silentLoginService.erLoggetPaaViaSilentLogin;
        return this.hentDokumentNumre(this.installationHttp, dokumentnummer).pipe(
            switchMap((numre: string[]) =>
                forkJoin(
                    numre.map((nr: string) =>
                        this.installationHttp.getUrl(`api/edh/documentid/${nr}?markerDokumentLaest=${markerDokumentLaest}`, ParametreForAktuelleInstallation.true)
                    )
                )
            )
        );
    }

    public hentDokumentNumre(httpClient: AppserverHttpClient, dokumentnummer: number): Observable<string[]> {
        // Tjek om ejendomNr er sat når der bruges installationHttp
        if (httpClient === this.installationHttp) {
            const credentials = this.installationCredentials.hent();
            if (!credentials || !credentials.ejendomNr) {
                return of([]);
            }
        }

        return httpClient.get(`api/edhbilag/dokumentid/${dokumentnummer}`, ParametreForAktuelleInstallation.true).pipe(
            map((result: any) => result.edhbilag.map((bilag: any) => bilag.dokumentid)),
            map((bilagsdokumentnumre: string[]) => [String(dokumentnummer)].concat(bilagsdokumentnumre))
        );
    }
}
